// Common validation utilities shared across all forms
export const isHalfWidthNumeric = (value: string): boolean => {
  return /^\d+$/.test(value);
};

export const isHalfOrFullWidthNumeric = (value: string): boolean => {
  // Return false for null, undefined, or non-string inputs for robustness
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  // Allow both half-width and full-width hyphens: - (U+002D) and － (U+FF0D)
  return /^[0-9０-９]+([-－][0-9０-９]+)*$/.test(value);
};

export const isHalfWidthKatakana = (value: string): boolean => {
  return /^[ｦ-ﾟ\s]*$/.test(value);
};

/**
 * Converts full-width katakana to half-width katakana
 * @param value - The input string that may contain full-width katakana
 * @returns The converted string with half-width katakana
 */
export const convertToHalfWidthKatakana = (value: string): string => {
  if (!value) return value;

  // Full-width Katakana/U+30A0–U+30FF → Half-width Katakana/U+FF61–U+FF9F
  const map: Record<string, string> = {
    // Basic vowels
    ア: 'ｱ',
    イ: 'ｲ',
    ウ: 'ｳ',
    エ: 'ｴ',
    オ: 'ｵ',
    // K-row
    カ: 'ｶ',
    キ: 'ｷ',
    ク: 'ｸ',
    ケ: 'ｹ',
    コ: 'ｺ',
    // S-row
    サ: 'ｻ',
    シ: 'ｼ',
    ス: 'ｽ',
    セ: 'ｾ',
    ソ: 'ｿ',
    // T-row
    タ: 'ﾀ',
    チ: 'ﾁ',
    ツ: 'ﾂ',
    テ: 'ﾃ',
    ト: 'ﾄ',
    // N-row
    ナ: 'ﾅ',
    ニ: 'ﾆ',
    ヌ: 'ﾇ',
    ネ: 'ﾈ',
    ノ: 'ﾉ',
    // H-row
    ハ: 'ﾊ',
    ヒ: 'ﾋ',
    フ: 'ﾌ',
    ヘ: 'ﾍ',
    ホ: 'ﾎ',
    // M-row
    マ: 'ﾏ',
    ミ: 'ﾐ',
    ム: 'ﾑ',
    メ: 'ﾒ',
    モ: 'ﾓ',
    // Y-row
    ヤ: 'ﾔ',
    ユ: 'ﾕ',
    ヨ: 'ﾖ',
    // R-row
    ラ: 'ﾗ',
    リ: 'ﾘ',
    ル: 'ﾙ',
    レ: 'ﾚ',
    ロ: 'ﾛ',
    // W-row
    ワ: 'ﾜ',
    ヲ: 'ｦ',
    ン: 'ﾝ',
    // Dakuten/handakuten precomposed
    ガ: 'ｶﾞ',
    ギ: 'ｷﾞ',
    グ: 'ｸﾞ',
    ゲ: 'ｹﾞ',
    ゴ: 'ｺﾞ',
    ザ: 'ｻﾞ',
    ジ: 'ｼﾞ',
    ズ: 'ｽﾞ',
    ゼ: 'ｾﾞ',
    ゾ: 'ｿﾞ',
    ダ: 'ﾀﾞ',
    ヂ: 'ﾁﾞ',
    ヅ: 'ﾂﾞ',
    デ: 'ﾃﾞ',
    ド: 'ﾄﾞ',
    バ: 'ﾊﾞ',
    ビ: 'ﾋﾞ',
    ブ: 'ﾌﾞ',
    ベ: 'ﾍﾞ',
    ボ: 'ﾎﾞ',
    パ: 'ﾊﾟ',
    ピ: 'ﾋﾟ',
    プ: 'ﾌﾟ',
    ペ: 'ﾍﾟ',
    ポ: 'ﾎﾟ',
    // Small kana, sokuon
    ァ: 'ｧ',
    ィ: 'ｨ',
    ゥ: 'ｩ',
    ェ: 'ｪ',
    ォ: 'ｫ',
    ャ: 'ｬ',
    ュ: 'ｭ',
    ョ: 'ｮ',
    ッ: 'ｯ',
    // Special/others
    ヴ: 'ｳﾞ',
    ヮ: 'ﾜ',
    ヵ: 'ｶ',
    ヶ: 'ｹ',
    ー: 'ｰ',
    '・': '･',
    '「': '｢',
    '」': '｣',
    '、': '､',
    '。': '｡',
  };

  return value.replaceAll(/[\u30A0-\u30FF]/g, (ch: string) => map[ch] ?? ch);
};

export const isValidEmail = (email: string): boolean => {
  // ReDoS-safe email validation - avoids catastrophic backtracking
  // Uses specific character classes instead of negated character classes with quantifiers
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

export const isValidPostalCode = (part1: string, part2: string): boolean => {
  return (
    isHalfWidthNumeric(part1) &&
    part1.length === 3 &&
    isHalfWidthNumeric(part2) &&
    part2.length === 4
  );
};

// Common field validation results
export type FieldValidationResult = { isValid: true } | { isValid: false; error: string };
export interface ValidationError {
  field: string;
  message: string;
}

export const isValidNoharaEmail = (email: string): boolean => {
  const requiredDomain = '@nohara-inc.co.jp';

  // Normalize email to lowercase for case-insensitive comparison
  const lowerCaseEmail = email.toLowerCase();

  // validate email format and validate domain
  return isValidEmail(lowerCaseEmail) && lowerCaseEmail.endsWith(requiredDomain);
};
